import React, { useMemo } from 'react';
import {
  FormControlLabel,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormLabel,
  FormGroup,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  titleSettingsCard: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
  },
  module_title: {
    color: theme.palette.texts.secondary,
    fontSize: theme.typography.h6.fontSize,
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    marginLeft: '10px',
    '&.Mui-focused': {
      color: theme.palette.texts.secondary,
    },
  },
  module_report_container: {
    margin: ' 0',
  },
  classes_container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    width: '100%',
    margin: '2% 1%',
  },
  form_control: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    marginTop: '14px',
  },
}));

function ReportModule({ title, options, report, updateReportConfig }) {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <FormControl component='fieldset' className={classes.form_control}>
      <FormLabel component='legend' className={classes.module_title}>
        {t(title)}
      </FormLabel>
      <FormGroup className={classes.classes_container}>
        {options.map(({ key, label, optional }) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                size='small'
                checked={!!report.lesions_to_show?.[key]}
                onChange={updateReportConfig}
                name={key}
                disabled={!optional && report.lesions_to_show?.[key]}
              />
            }
            label={t(label)}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
}

function DiagnosticsModule({ report, updateReportConfig, updateReportField }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const premium_features = useSelector((state) => state.global_settings.premium_features);

  const somePremiumDiagnostics = useMemo(
    () => premium_features.diagnostics_microcalc_module || premium_features.diagnostics_opacities_module,
    [premium_features]
  );

  return (
    <Grid container direction='column' spacing={1}>
      <Grid item>
        <Card elevation={1}>
          <CardHeader
            disableTypography
            title={
              <Typography className={classes.titleSettingsCard} variant='h6'>
                {t('report.quality_report')}
              </Typography>
            }
            style={{ padding: '0px 16px', margin: 0, width: '100%' }}
          />
          <CardContent
            style={{
              padding: '10px 18px',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: '100%',
            }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!report.contour_shown}
                  onChange={updateReportConfig}
                  name='contour_shown'
                />
              }
              label={t('report.show_contours')}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item>
        <Card elevation={1}>
          <CardHeader
            disableTypography
            title={
              <Typography className={classes.titleSettingsCard} variant='h6'>
                {t('Settings.projections')}
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: 0 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!report.projections_big}
                  onChange={updateReportConfig}
                  name='projections_big'
                />
              }
              label={t('Settings.projections_big')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!report.crop_to_breast}
                  onChange={updateReportConfig}
                  name='crop_to_breast'
                />
              }
              label={t('Settings.crop_to_breast')}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item>
        <Card elevation={1}>
          <CardHeader
            disableTypography
            title={
              <Typography className={classes.titleSettingsCard} variant='h6'>
                {t('report.report_modules_title')}
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: 0 }}>
            {premium_features.diagnostics_opacities_module && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!report.show_missed_findings}
                    onChange={updateReportConfig}
                    name='show_missed_findings'
                  />
                }
                label={t('Settings.show_missed_findings')}
              />
            )}
            <FormControl fullWidth style={{ marginTop: 8, maxWidth: 260 }}>
              <InputLabel id='file-format-label'>
                {t('Settings.report_file_format', { defaultValue: 'File format' })}
              </InputLabel>
              <Select
                labelId='file-format-label'
                name='file_format'
                value={report.file_format}
                onChange={(e) => updateReportField(e.target.name, e.target.value)}>
                <MenuItem value='pdf'>PDF</MenuItem>
                <MenuItem value='jpeg'>JPEG</MenuItem>
              </Select>
            </FormControl>

            {somePremiumDiagnostics && (
              <div className={classes.module_report_container}>
                {premium_features.diagnostics_microcalc_module && (
                  <ReportModule
                    title='ResultView.diagnosticsMicrocalc'
                    options={[
                      { key: 'calc_vessel', label: 'projections.vessels', optional: true },
                      { key: 'calc_birads2', label: 'projections.birads_2', optional: true },
                      { key: 'calc_birads3', label: 'projections.birads_3', optional: false },
                      { key: 'calc_birads4', label: 'projections.birads_4', optional: false },
                      { key: 'calc_birads5', label: 'projections.birads_5', optional: false },
                    ]}
                    report={report}
                    updateReportConfig={updateReportConfig}
                  />
                )}

                {premium_features.diagnostics_opacities_module && (
                  <ReportModule
                    title='ResultView.diagnosticsLesions'
                    options={[
                      { key: 'opac_lesionKnown', label: 'projections.lesionKnown', optional: true },
                      { key: 'opac_birads2', label: 'projections.birads_2', optional: true },
                      { key: 'opac_birads3', label: 'projections.birads_3', optional: false },
                      { key: 'opac_birads4', label: 'projections.birads_4', optional: false },
                      { key: 'opac_birads5', label: 'projections.birads_5', optional: false },
                    ]}
                    report={report}
                    updateReportConfig={updateReportConfig}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default DiagnosticsModule;
